import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  recognizeStrokes,
  DRAW_INDEX_SIZE,
  type Match,
  type Point,
  type Stroke,
} from "@/lib/draw-recognize";
import { copyText } from "@/lib/copy";

/**
 * Pointer-driven sketch pad. Strokes are matched against the build-time glyph
 * shape index entirely on-device (see src/lib/draw-recognize.ts) — no network.
 */
export function DrawPad() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const currentRef = useRef<Stroke | null>(null);
  const drawingRef = useRef(false);

  const [results, setResults] = useState<Match[]>([]);
  const [hasInk, setHasInk] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = getComputedStyle(canvas).getPropertyValue("color") || "#fff";
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    const all = [...strokesRef.current];
    if (currentRef.current) all.push(currentRef.current);
    for (const stroke of all) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
  }, []);

  useEffect(() => {
    setupCanvas();
    redraw();
    const onResize = () => {
      setupCanvas();
      redraw();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setupCanvas, redraw]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    currentRef.current = [pointFromEvent(e)];
    redraw();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !currentRef.current) return;
    currentRef.current.push(pointFromEvent(e));
    redraw();
  };

  const endStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentRef.current && currentRef.current.length > 0) {
      strokesRef.current.push(currentRef.current);
    }
    currentRef.current = null;
    setHasInk(strokesRef.current.length > 0);
    setResults(recognizeStrokes(strokesRef.current));
  };

  const clear = () => {
    strokesRef.current = [];
    currentRef.current = null;
    setHasInk(false);
    setResults([]);
    redraw();
  };

  const undo = () => {
    strokesRef.current.pop();
    setHasInk(strokesRef.current.length > 0);
    setResults(strokesRef.current.length ? recognizeStrokes(strokesRef.current) : []);
    redraw();
  };

  const copy = async (m: Match) => {
    const ok = await copyText(m.symbol, { source: "symbol-tile", symbol: m.symbol });
    if (!ok) return;
    setCopied(m.slug);
    window.setTimeout(() => setCopied((c) => (c === m.slug ? null : c)), 1200);
  };

  return (
    <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          aria-label="Drawing canvas — sketch a symbol"
          className="glass aspect-square w-full touch-none rounded-xl text-foreground"
        />
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={undo}
            disabled={!hasInk}
            className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={!hasInk}
            className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            Clear
          </button>
          <span className="ml-auto text-xs text-muted-foreground">
            Matching against {DRAW_INDEX_SIZE} symbols
          </span>
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold">Matches</h2>
        {results.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {hasInk
              ? "No close matches — try drawing the shape larger or with cleaner strokes."
              : "Draw a shape on the canvas and the closest symbols appear here."}
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {results.map((m) => (
              <li key={m.slug} className="glass flex items-center gap-2 rounded-lg p-2">
                <Link
                  to="/symbol/$slug"
                  params={{ slug: m.slug }}
                  className="flex min-w-0 flex-1 items-center gap-2 hover:opacity-90"
                  title={m.name}
                >
                  <span className="text-2xl leading-none">{m.symbol}</span>
                  <span className="min-w-0 truncate text-xs text-muted-foreground">{m.name}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => copy(m)}
                  aria-label={`Copy ${m.name}`}
                  className={`flex-none rounded border border-border px-1.5 py-1 text-[10px] uppercase tracking-wider ${
                    copied === m.slug
                      ? "text-accent"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {copied === m.slug ? "✓" : "Copy"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
